/**
 * Reports — recruitment analytics. Tabs: the pipeline Funnel (current
 * occupancy), cohort Conversion (reached-stage + stage-to-stage rates),
 * Screening (disqualification analytics at the three gates — Intake /
 * Questionnaire / Prescreen), Calls (per-recruiter calling), Productivity
 * (daily recruiter activity), and Metrics (headline sales KPIs). Each is
 * sliceable overall / by study / by source / by recruiter (where it applies)
 * with a time range, and a GLOBAL study selector scopes every tab. Selections
 * live in the URL (shareable) and persist via getPref.
 *
 * Custom is the report builder (reportBuilder/, plan B4.6), open to every CRM
 * member. It brings its own dates, study and other filters, so the page's
 * toolbar hides there, and its address is the builder's own
 * (?tab=custom&report=<id>: ?report= is the saved report it opens). It mounts
 * on the first visit and then stays mounted, hidden on the other tabs, so a
 * report being built survives a look at another tab.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, SearchCheck } from 'lucide-react';
import api from '../../services/api';
import { StudyOption, studyInternalLabel } from '../types';
import { getPref, setPref, rangeWindow, useIsCRMAdmin } from '../lib';
import FunnelReport from '../components/reports/FunnelReport';
import StageConversionReport from '../components/reports/StageConversionReport';
import DQReport, { DQGroupBy } from '../components/reports/DQReport';
import ScreeningGateReport from '../components/reports/ScreeningGateReport';
import CallsReport from '../components/reports/CallsReport';
import ProductivityReport from '../components/reports/ProductivityReport';
import DispositionsReport from '../components/reports/DispositionsReport';
import MetricsReport from '../components/reports/MetricsReport';
import CommsFeedReport from '../components/reports/CommsFeedReport';
import Referrals from '../components/reports/Referrals';
import AdSpendReport from '../components/reports/AdSpendReport';
import ReportBuilder from '../reportBuilder/ReportBuilder';
import { FilterBar, FilterSelect, PageHeader, SegmentedControl, SegmentedTabs } from '../components';

const TABS = [
  { key: 'funnel', label: 'Funnel' },
  { key: 'conversion', label: 'Conversion' },
  { key: 'screening', label: 'Screening' },
  { key: 'dispositions', label: 'Dispositions' },
  { key: 'calls', label: 'Calls' },
  { key: 'productivity', label: 'Productivity' },
  { key: 'metrics', label: 'Metrics' },
  // The report builder, for every CRM member (plan A4.2, B4.6). The key stays
  // 'custom': quick search's Reports > Custom and the builder's own addresses
  // link ?tab=custom.
  { key: 'custom', label: 'Custom' },
] as const;

// The three disqualification gates inside the Screening tab. Intake +
// Questionnaire are the new ScreeningGateReport; Prescreen is the DQ ledger.
const SCREENING_GATES = [
  { value: 'intake', label: 'Intake' },
  { value: 'questionnaire', label: 'Questionnaire' },
  { value: 'prescreen', label: 'Prescreen' },
] as const;

// The Funnel tab's group-by vocabulary. Conversion used to share this
// exact array; it no longer does -- see CONVERSION_GROUPS.
const FUNNEL_GROUPS = [
  { value: 'overall', label: 'Overall' },
  { value: 'study', label: 'By study' },
  { value: 'lead_source', label: 'By lead source' },
  { value: 'recruiter', label: 'By recruiter' },
] as const;

// Conversion gets the same four PLUS the three Meta ad dimensions, and the
// Funnel tab must never offer them. The two RPCs read different tables:
// crm.stage_conversion_stats groups on the contact's first-touch
// crm.contact_leads row, which carries ad_campaign_id / ad_set_id / ad_id;
// crm.funnel_stats reads crm.contacts, which carries none of them and
// RAISE EXCEPTIONs on a group_by it does not know. Offering "By campaign" on
// the Funnel tab would therefore render a 502, not an empty table.
// Labels are raw Meta ids until the entity sync lands and can name them.
// Plan: docs/crm-ad-spend-roi-plan.md 4.1 / 7.1.
const CONVERSION_GROUPS = [
  ...FUNNEL_GROUPS,
  { value: 'campaign', label: 'By campaign' },
  { value: 'adset', label: 'By ad set' },
  { value: 'ad', label: 'By ad' },
  // Landing variants (docs/crm-landing-variants-plan.md): groups on the
  // contact's first-touch contact_leads.landing_variant, same table the ad
  // dimensions read — so it too is Conversion-only (crm.funnel_stats would
  // RAISE on it, turning a clean 400 into a 502).
  { value: 'landing_variant', label: 'By landing variant' },
] as const;

const DQ_GROUPS = [
  { value: 'category', label: 'By reason category' },
  { value: 'criterion', label: 'By criterion' },
  { value: 'study', label: 'By study' },
  { value: 'lead_source', label: 'By lead source' },
  { value: 'recruiter', label: 'By recruiter' },
] as const;

const RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
] as const;

const FUNNEL_GROUP_TABS = new Set(['funnel', 'conversion']);

export default function Reports() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [studies, setStudies] = useState<StudyOption[]>([]);

  // The live communications board is admin-only — it surfaces message bodies +
  // patient names (PHI). Non-admins never see the tab, and the backend gates the
  // endpoint with requireCrmAdmin, so a hand-typed ?tab=live falls back to Funnel.
  // Referrals is admin-only for the same reason in reverse: O-14's default-closed
  // choice, pinned client-side here AND by the endpoint's 403 for members.
  const isAdmin = useIsCRMAdmin();
  // Ad spend is admin-only, owner-confirmed 2026-08-18 (§13.4): it is the only
  // surface in the CRM that shows money, and the two endpoints behind it carry
  // requireCrmAdmin, so a hand-typed ?tab=adspend falls back to Funnel.
  const TAB_LIST = isAdmin
    ? [...TABS, { key: 'adspend', label: 'Ad spend' }, { key: 'referrals', label: 'Referrals' }, { key: 'live', label: 'Live feed' }]
    : [...TABS];

  const tab = TAB_LIST.some((t) => t.key === params.get('tab')) ? params.get('tab')! : 'funnel';
  const isCustom = tab === 'custom';
  // The builder mounts on the first visit to Custom and then stays mounted,
  // hidden on the other tabs: what is being built (unsaved settings, the open
  // report) survives a look at another tab. Nothing in it polls.
  const [customVisited, setCustomVisited] = useState(isCustom);
  useEffect(() => {
    if (isCustom) setCustomVisited(true);
  }, [isCustom]);
  const usesFunnelGroups = FUNNEL_GROUP_TABS.has(tab);
  const range = RANGES.some((r) => r.value === params.get('range'))
    ? params.get('range')!
    : getPref('reports.range', '30');

  // Screening gate (Intake / Questionnaire / Prescreen) — persisted across tabs.
  const gateParam = params.get('gate') || getPref('reports.screening.gate', 'intake');
  const gate = SCREENING_GATES.some((g) => g.value === gateParam) ? gateParam : 'intake';
  const isPrescreen = tab === 'screening' && gate === 'prescreen';

  // Group-by applies to funnel/conversion (funnel groups) and the prescreen gate
  // (DQ groups). Its pref key + default depend on which of those is active.
  const groupPrefKey = usesFunnelGroups ? `reports.${tab}.group` : 'reports.screening.prescreen.group';
  const groupDefault = usesFunnelGroups ? 'overall' : 'category';
  const groupParam = params.get('group') || getPref(groupPrefKey, groupDefault);
  // Which of the two funnel-family vocabularies this tab is allowed to use.
  // setParam already drops ?group= on a tab change, so this guard is for the
  // hand-typed / shared link: ?tab=funnel&group=campaign falls back to
  // 'overall' here rather than reaching crm.funnel_stats and 502-ing.
  const funnelGroupOptions = tab === 'conversion' ? CONVERSION_GROUPS : FUNNEL_GROUPS;
  const funnelGroup = funnelGroupOptions.some((g) => g.value === groupParam) ? groupParam : 'overall';
  const dqGroup = DQ_GROUPS.some((g) => g.value === groupParam) ? (groupParam as DQGroupBy) : 'category';

  // Global study filter (scopes every tab). Persisted across tabs.
  const studyId = params.get('study') || getPref('reports.study', '') || '';

  // The assignable list of real (crm.studies) studies for the global selector.
  useEffect(() => {
    let alive = true;
    api
      .get('/api/crm/studies')
      .then((res) => {
        if (!alive) return;
        const list: StudyOption[] = (res.data?.studies || res.data || []).filter((s: StudyOption) => s.id);
        setStudies(list);
      })
      .catch(() => { /* selector just stays "All studies" */ });
    return () => { alive = false; };
  }, []);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // The group default depends on tab+gate, so reset it when either changes.
    if (key === 'tab' || key === 'gate') next.delete('group');
    // Custom's address is the builder's own, which drops the range and the
    // study, so they land in the URL again on the way to any other tab and a
    // copied link still reproduces the view. ?report= stays (only Custom reads
    // it), so the hidden builder keeps the report it has open.
    if (key === 'tab' && value !== 'custom') {
      if (!next.get('range')) next.set('range', range);
      if (!next.get('study') && studyId) next.set('study', studyId);
    }
    setParams(next, { replace: true });
    if (key === 'range') setPref('reports.range', value);
    if (key === 'group') setPref(groupPrefKey, value);
    if (key === 'study') setPref('reports.study', value);
    if (key === 'gate') setPref('reports.screening.gate', value);
  };

  // Persisted defaults still land in the URL so copied links reproduce the view.
  // Not on Custom: its address is the builder's own (?tab=custom&report=<id>).
  useEffect(() => {
    if (!isCustom && (!params.get('range') || !params.get('tab') || (!params.get('study') && studyId))) {
      const next = new URLSearchParams(params);
      if (!next.get('tab')) next.set('tab', tab);
      if (!next.get('range')) next.set('range', range);
      if (!next.get('study') && studyId) next.set('study', studyId);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconcile a persisted/shared study filter that doesn't exist in this
  // network's study list (a deleted study, or a cross-network ?study=<id> link):
  // clear it so the picker ("All studies") and the actual report scope agree.
  // Only after a SUCCESSFUL, non-empty fetch — a transient fetch failure leaves
  // studies=[] and must not silently drop a valid filter.
  useEffect(() => {
    if (studyId && studies.length > 0 && !studies.some((s) => s.id === studyId)) {
      setParam('study', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studies, studyId]);

  const { from, to } = useMemo(() => rangeWindow(range), [range]);

  const sid = studyId || null;
  const showGroup = usesFunnelGroups || isPrescreen;
  const groups = usesFunnelGroups ? funnelGroupOptions : DQ_GROUPS;
  const group = usesFunnelGroups ? funnelGroup : dqGroup;
  // The referrals report takes only the time window — no study or group params —
  // so the global study selector hides there rather than sitting inert and lying.
  const studyScoped = tab !== 'referrals';

  // Reports → Search cross-link (search plan §13 P4): open the builder seeded
  // with this view's scope. The DQ/disposition kinds carry the study + time
  // window natively; every other tab seeds the current-study step (so the
  // button needs a study selected there). Nothing saves until the user does.
  const studySlug = studies.find((s) => s.id === studyId)?.slug || null;
  const searchSeed = useMemo(() => {
    if (tab === 'referrals') return null; // referral analytics is not lead-centric — nothing to seed
    const windowed = { ...(from ? { from } : {}), ...(to ? { to } : {}) };
    const base = { exclude_archived: true, exclude_test: true };
    if (isPrescreen) {
      return {
        name: 'Prescreen DQs',
        definition: {
          version: 1, base,
          steps: [{ id: 's_dq', mode: 'include', match: 'any', conditions: [{ kind: 'dq', ...(studySlug ? { study_slugs: [studySlug] } : {}), ...windowed }] }],
        },
      };
    }
    if (tab === 'dispositions') {
      return {
        name: 'Dispositioned leads',
        definition: {
          version: 1, base,
          steps: [{ id: 's_disp', mode: 'include', match: 'any', conditions: [{ kind: 'disposition', ...(studySlug ? { study_slugs: [studySlug] } : {}), ...windowed }] }],
        },
      };
    }
    if (!studySlug) return null; // funnel-family: nothing meaningful to seed without a study
    return {
      name: `${studies.find((s) => s.id === studyId)?.title || studySlug} leads`,
      definition: {
        version: 1, base,
        steps: [{ id: 's_study', mode: 'include', match: 'any', conditions: [{ kind: 'study', slugs: [studySlug], scope: 'current' }] }],
      },
    };
  }, [isPrescreen, tab, studySlug, studyId, studies, from, to]);

  return (
    <div className="space-y-4">
      <PageHeader
        icon={BarChart3}
        title="Reports"
        subtitle="Pipeline conversion, recruiter productivity, and the reasons leads don't qualify."
      />

      {/* Report-type tabs */}
      <SegmentedTabs
        label="Report type"
        tabs={TAB_LIST.map((t) => ({ value: t.key, label: t.label }))}
        value={tab}
        onChange={(v) => setParam('tab', v)}
      />

      {/* Toolbar: screening gate toggle + global study / group / range selects.
          One swipeable strip on phones (mobileScroll) — recruiters check
          numbers from their phone, so the controls must not stack. Custom
          brings its own dates and filters (the builder's panel), so the whole
          toolbar (study, group, time, Open as search) hides there. */}
      <FilterBar mobileScroll className={isCustom ? 'hidden' : undefined}>
        {tab === 'screening' && (
          <SegmentedControl
            label="Screening gate"
            value={gate}
            onChange={(v) => setParam('gate', v)}
            options={SCREENING_GATES.map((g) => ({ value: g.value, label: g.label }))}
          />
        )}
        {studyScoped && (
          <FilterSelect
            value={studyId}
            onChange={(v) => setParam('study', v)}
            auto
            label="Study"
            title="Scope every tab to one study (by each lead's current study)"
          >
            <option value="">All studies</option>
            {studies.map((s) => (
              <option key={s.id!} value={s.id!}>{studyInternalLabel(s)}</option>
            ))}
          </FilterSelect>
        )}
        {showGroup && (
          <FilterSelect value={group} onChange={(v) => setParam('group', v)} auto label="Group by">
            {groups.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </FilterSelect>
        )}
        <FilterSelect value={range} onChange={(v) => setParam('range', v)} auto label="Time range">
          {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </FilterSelect>
        {studyScoped && (
          <button
            type="button"
            onClick={() => searchSeed && navigate('/crm/search/new', { state: { seed: searchSeed.definition, name: searchSeed.name } })}
            disabled={!searchSeed}
            title={searchSeed
              ? 'Open the Search builder seeded with this view — drill into exactly who these leads are'
              : 'Pick a study to open this view as a search'}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
          >
            <SearchCheck className="h-3.5 w-3.5" />
            Open as search
          </button>
        )}
      </FilterBar>

      {/* Funnel and Conversion are BOTH reached-stage counts since 20260818140000
          and they cohort differently on purpose, so flipping between the tabs
          shows two numbers that will not always agree. Naming the cohort once,
          here, beats repeating it on every small-multiple card below. */}
      {(tab === 'funnel' || tab === 'conversion') && (
        <p className="-mt-1 text-xs text-gray-500 dark:text-gray-400">
          {tab === 'funnel'
            ? 'Counts leads by when they were created. Bars are everyone who ever reached a stage, so a lead that moved on still counts behind it.'
            : 'Counts leads by when they entered the pipeline, and adds stage-to-stage rates and time in stage.'}
        </p>
      )}

      {tab === 'funnel' ? (
        <FunnelReport groupBy={funnelGroup as any} from={from} to={to} studyId={sid} />
      ) : tab === 'conversion' ? (
        <StageConversionReport groupBy={funnelGroup as any} from={from} to={to} studyId={sid} />
      ) : tab === 'calls' ? (
        <CallsReport from={from} to={to} studyId={sid} />
      ) : tab === 'dispositions' ? (
        <DispositionsReport from={from} to={to} studyId={sid} />
      ) : tab === 'productivity' ? (
        <ProductivityReport from={from} to={to} studyId={sid} />
      ) : tab === 'metrics' ? (
        <MetricsReport from={from} to={to} studyId={sid} />
      ) : tab === 'live' ? (
        <CommsFeedReport from={from} to={to} studyId={sid} />
      ) : tab === 'adspend' ? (
        <AdSpendReport from={from} to={to} studyId={sid} />
      ) : tab === 'referrals' ? (
        <Referrals from={from} to={to} />
      ) : tab === 'screening' ? (
        gate === 'prescreen' ? (
          <DQReport groupBy={dqGroup} from={from} to={to} studyId={sid} />
        ) : (
          <ScreeningGateReport gate={gate as 'intake' | 'questionnaire'} from={from} to={to} studyId={sid} />
        )
      ) : null}

      {/* Custom: the report builder (plan B4.6), mounted on the first visit and
          then kept, hidden on the other tabs. ?report= is its initialReportId;
          the builder follows it when it changes and keeps the address in step
          itself (opening, saving, copying and archiving move ?report=). */}
      {(isCustom || customVisited) && (
        <div className={isCustom ? undefined : 'hidden'}>
          <ReportBuilder initialReportId={params.get('report')} />
        </div>
      )}
    </div>
  );
}
